import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import Logout from "../components/account/Logout";
import Login from "../components/account/Login";
import { setOriginalPath } from "../app/features/auth/authSlice";

import { useAppDispatch, useAppSelector } from "../app/hooks";
import { tokenLogin } from "../app/features/auth/authActions";
import Menu from "../components/menu/Menu";
import { getAllMenuItems, isPageShown } from "../components/menu/menuConfig";
import { Roles } from "../app/features/auth/authTypes";
import ResetPassword from "../components/account/ResetPassword";

type PrivateRouteProps = {
  restrictedTo?: Roles[];
  developerOnly?: boolean;
};

function PrivateRoute({ restrictedTo, developerOnly }: PrivateRouteProps) {
  const authenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const activeRole = useAppSelector((state) => state.auth.role);
  const isDeveloper = useAppSelector((state) => state.auth.isDeveloper);
  const appropriate = restrictedTo
    ? isPageShown(
        { roles: restrictedTo, developerOnly },
        activeRole,
        isDeveloper,
      )
    : true;
  const dispatch = useAppDispatch();

  if (!authenticated) {
    dispatch(setOriginalPath(document.location.pathname));
    return <Navigate to="/login" />;
  }

  if (!appropriate) {
    return <Navigate to="/" />;
  }

  return <Outlet />;
}

const AuthRouter = () => {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);
  const currentRole = useAppSelector((state) => state.auth.role);
  const lastAuthenticationTicks = useAppSelector((state) => state.auth.since);
  const lastAuthentication = lastAuthenticationTicks
    ? new Date(lastAuthenticationTicks)
    : new Date(2000, 1, 1);
  const menu = getAllMenuItems();

  if (token) {
    const numberHours =
      (new Date().getTime() - lastAuthentication.getTime()) / (1000 * 3600);
    if (numberHours < 1) {
      //console.log("Recent authentication found.");
    } else {
      dispatch(tokenLogin(token, currentRole));
    }
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/logout" Component={Logout} />
        <Route path="/login" Component={Login} />
        <Route path="/reset-password" Component={ResetPassword} />
        <Route
          path="/"
          element={
            <Menu>
              <Outlet />
            </Menu>
          }
        >
          {menu.map((menuItem) => (
            <Route
              key={menuItem.path}
              path={menuItem.path}
              element={
                <PrivateRoute
                  restrictedTo={menuItem.roles}
                  developerOnly={menuItem.developerOnly}
                />
              }
            >
              <Route path={menuItem.path} element={menuItem.component} />
            </Route>
          ))}
          <Route path="*" element={<Navigate to="/construct" />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default AuthRouter;
